import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Eye, EyeOff, Plug, PlugZap, ExternalLink, CheckCircle2, AlertCircle, Loader2, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GoogleAdsSettingsCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [form, setForm] = useState({
    developer_token: '',
    client_id: '',
    client_secret: '',
    login_customer_id: '',
  });
  const [testResult, setTestResult] = useState(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/google-ads/config`, { withCredentials: true });
      setStatus(data);
      // Pre-fill form fields with masked indicators (we don't get back values, just bools)
      setForm((f) => ({
        ...f,
        login_customer_id: data.login_customer_id || '',
      }));
    } catch (e) {
      toast.error("Erreur de chargement de la config Google Ads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    // Detect OAuth callback redirect (?gads=connected | ?gads_error=...)
    const params = new URLSearchParams(window.location.search);
    if (params.get('gads') === 'connected') {
      toast.success('Google Ads connecté avec succès !');
      window.history.replaceState({}, '', window.location.pathname);
    }
    const err = params.get('gads_error');
    if (err) {
      const details = params.get('details');
      toast.error(`Erreur OAuth Google Ads: ${err}${details ? ` — ${decodeURIComponent(details)}` : ''}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [reload]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      // Only send fields that are not blank (allow partial updates)
      const payload = {};
      if (form.developer_token) payload.developer_token = form.developer_token;
      if (form.client_id) payload.client_id = form.client_id;
      if (form.client_secret) payload.client_secret = form.client_secret;
      if (form.login_customer_id !== '') payload.login_customer_id = form.login_customer_id;
      if (Object.keys(payload).length === 0) {
        toast.info('Aucun champ à enregistrer');
        return;
      }
      await axios.put(`${API}/google-ads/config`, payload, { withCredentials: true });
      toast.success('Configuration enregistrée');
      // Clear secret fields from form for safety
      setForm((f) => ({ ...f, developer_token: '', client_id: '', client_secret: '' }));
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const startOAuth = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.get(`${API}/google-ads/oauth/start`, { withCredentials: true });
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'initialisation OAuth");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Déconnecter Google Ads ? Vous devrez ré-autoriser pour récupérer des données.')) return;
    try {
      await axios.delete(`${API}/google-ads/disconnect`, { withCredentials: true });
      toast.success('Google Ads déconnecté');
      reload();
    } catch (e) {
      toast.error('Erreur lors de la déconnexion');
    }
  };

  const testConnection = async () => {
    setTestResult({ loading: true });
    try {
      const { data } = await axios.get(`${API}/google-ads/test-connection`, { withCredentials: true });
      setTestResult(data);
      if (data.success) {
        toast.success(`Connexion OK — ${data.accessible_customers?.length || 0} compte(s) accessible(s)`);
      } else {
        toast.error(`Test échoué: ${data.error}`);
      }
    } catch (e) {
      setTestResult({ success: false, error: e.response?.data?.detail || e.message });
      toast.error('Test échoué');
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-neutral-200 rounded-lg p-6">
        <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
      </div>
    );
  }

  const allConfigured = status?.has_developer_token && status?.has_client_id && status?.has_client_secret && status?.has_login_customer_id;
  const isConnected = status?.is_connected;

  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-6 space-y-5" data-testid="google-ads-settings-card">
      <div className="flex items-center gap-2">
        <PlugZap className="w-5 h-5 text-orange-600" />
        <h2 className="font-heading font-semibold text-lg text-neutral-900">Google Ads (SEA)</h2>
        {isConnected ? (
          <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100" data-testid="gads-status-connected">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Connecté
          </Badge>
        ) : allConfigured ? (
          <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100" data-testid="gads-status-pending">
            <AlertCircle className="w-3 h-3 mr-1" /> Prêt à connecter
          </Badge>
        ) : (
          <Badge variant="outline" data-testid="gads-status-not-configured">À configurer</Badge>
        )}
      </div>

      <p className="text-sm text-neutral-600">
        Connectez votre compte gestionnaire (MCC) Google Ads pour récupérer les métriques SEA de tous vos clients sous-comptes.
      </p>

      {/* Status indicators */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <StatusRow label="Developer Token" ok={status?.has_developer_token} />
        <StatusRow label="MCC Customer ID" ok={status?.has_login_customer_id} value={status?.login_customer_id} />
        <StatusRow label="OAuth Client ID" ok={status?.has_client_id} />
        <StatusRow label="OAuth Client Secret" ok={status?.has_client_secret} />
      </div>

      {isConnected && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
          <p className="text-green-800">
            <strong>Compte connecté :</strong> {status.connected_email || '(email inconnu)'}
          </p>
          {status.connected_at && (
            <p className="text-green-700 text-xs mt-0.5">Depuis le {new Date(status.connected_at).toLocaleString('fr-FR')}</p>
          )}
        </div>
      )}

      {/* Redirect URI hint */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs">
        <p className="text-blue-900 font-medium mb-1">URI de redirection à autoriser dans GCP Console :</p>
        <code className="text-blue-800 break-all bg-white px-2 py-1 rounded border border-blue-100" data-testid="gads-redirect-uri">
          {status?.redirect_uri}
        </code>
      </div>

      {/* Form (only show editable fields) */}
      <div className="space-y-3 pt-2 border-t border-neutral-100">
        <div className="space-y-1.5">
          <Label className="text-xs">Developer Token (laisser vide pour conserver l'actuel)</Label>
          <Input
            placeholder={status?.has_developer_token ? '••••••••••' : 'Coller le Developer Token'}
            value={form.developer_token}
            onChange={(e) => setForm({ ...form, developer_token: e.target.value })}
            data-testid="gads-developer-token-input"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">OAuth Client ID</Label>
          <Input
            placeholder={status?.has_client_id ? 'Configuré (laisser vide pour conserver)' : 'xxx.apps.googleusercontent.com'}
            value={form.client_id}
            onChange={(e) => setForm({ ...form, client_id: e.target.value })}
            data-testid="gads-client-id-input"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">OAuth Client Secret</Label>
          <div className="relative">
            <Input
              type={showSecret ? 'text' : 'password'}
              placeholder={status?.has_client_secret ? '••••••••' : 'GOCSPX-xxxxxxxxx'}
              value={form.client_secret}
              onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
              className="pr-10"
              data-testid="gads-client-secret-input"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
              onClick={() => setShowSecret(!showSecret)}
              tabIndex={-1}
            >
              {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">MCC Customer ID (sans tirets)</Label>
          <Input
            placeholder="7946528760"
            value={form.login_customer_id}
            onChange={(e) => setForm({ ...form, login_customer_id: e.target.value })}
            data-testid="gads-mcc-id-input"
          />
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={saveConfig} disabled={saving} className="bg-[#002FA7] hover:bg-[#002277]" data-testid="gads-save-config-btn">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>

          {allConfigured && !isConnected && (
            <Button onClick={startOAuth} disabled={connecting} className="bg-orange-600 hover:bg-orange-700" data-testid="gads-connect-btn">
              {connecting ? 'Redirection…' : (
                <>
                  <Plug className="w-4 h-4 mr-1" />
                  Connecter Google Ads
                </>
              )}
            </Button>
          )}

          {isConnected && (
            <>
              <Button variant="outline" onClick={testConnection} data-testid="gads-test-btn">
                Tester la connexion
              </Button>
              <Button variant="outline" onClick={startOAuth} data-testid="gads-reconnect-btn">
                Reconnecter
              </Button>
              <Button variant="outline" onClick={disconnect} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="gads-disconnect-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Déconnecter
              </Button>
            </>
          )}
        </div>

        {testResult && !testResult.loading && (
          <div className={`text-xs p-2 rounded ${testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {testResult.success
              ? `✓ ${testResult.accessible_customers?.length || 0} compte(s) accessible(s) : ${testResult.accessible_customers?.join(', ') || '—'}`
              : `✗ ${testResult.error}`}
          </div>
        )}
      </div>

      <div className="text-xs text-neutral-400 pt-2 border-t border-neutral-100">
        <a
          href="https://console.cloud.google.com/apis/credentials"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-blue-600 hover:underline"
        >
          Ouvrir Google Cloud Console <ExternalLink className="w-3 h-3" />
        </a>
        {' · '}
        <a
          href="https://ads.google.com/aw/apicenter"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-blue-600 hover:underline"
        >
          Centre API Google Ads <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}

function StatusRow({ label, ok, value }) {
  return (
    <div className="flex items-center gap-2">
      {ok ? (
        <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
      ) : (
        <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
      )}
      <span className="text-neutral-600">{label}</span>
      {value && <span className="text-neutral-900 font-mono text-xs">{value}</span>}
    </div>
  );
}
